import { apiService } from '@/shared/services/apiServices';
import { AxiosError } from 'axios';
import { OFFER_ERROR_MESSAGES } from '../errors';
import { format } from 'date-fns';
import { DiscountType } from '../types/offerListType';

// ============================================================================
// API request payload (backend contract)
// ============================================================================

export interface EditOfferPayload {
  offerName?: string;
  subscriptionPlanIds?: string[];
  discountType?: DiscountType;
  discountValue?: number;
  startDate?: string; // YYYY-MM-DD
  endDate?: string; // YYYY-MM-DD
  status?: string | undefined;
}

// ============================================================================
// Input from form (UI values)
// ============================================================================

export interface EditOfferInput {
  offerId: string;
  offerName: string;
  subscriptionPlanIds: string[];
  discountType: string; // "Procentowy"
  discountValue: string;
  startDate?: Date | undefined;
  endDate?: Date | undefined;
  status?: string;
}

// ============================================================================
// Result
// ============================================================================

export interface EditOfferResult {
  success: boolean;
  data?: unknown;
  error?: string;
  fieldErrors?: Record<string, string>;
}

// ============================================================================
// Mappers
// ============================================================================

const mapDiscountType = (type: string): DiscountType => {
  return type === 'Procentowy' ? 'PERCENTAGE' : 'FIXED';
};

const parseDiscountValue = (value: string): number => {
  const cleaned = (value || '').replace(/\s/g, '').replace(',', '.');
  const val = parseFloat(cleaned);
  return Number.isNaN(val) ? 0 : val;
};

// ============================================================================
// Error extraction
// ============================================================================

interface ApiErrorData {
  message?: string;
  error?: string;
  statusCode?: number;
  code?: string;
  details?: Array<{ path?: string; message?: string; code?: string }>;
}

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401) {
    return OFFER_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return OFFER_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }

  if (status === 400) {
    const msg = getMessageFromPayload(errorData);
    const fieldErrors = getFieldErrorsFromPayload(errorData);
    const firstDetailMessage =
      fieldErrors && Object.keys(fieldErrors).length > 0
        ? Object.values(fieldErrors)[0]
        : null;
    return firstDetailMessage || msg || OFFER_ERROR_MESSAGES.VALIDATION_ERROR;
  }

  const msg = getMessageFromPayload(errorData);
  return msg || OFFER_ERROR_MESSAGES.DEFAULT_ERROR;
};

function getFieldErrorsFromPayload(
  errorData: unknown
): Record<string, string> | undefined {
  if (!errorData || typeof errorData !== 'object') {
    return undefined;
  }
  const obj = errorData as ApiErrorData;
  const details = obj.details;
  if (!Array.isArray(details) || details.length === 0) {
    return undefined;
  }

  const fieldErrors: Record<string, string> = {};
  for (const d of details) {
    const path = d?.path;
    const message = d?.message;
    if (path && message) {
      fieldErrors[path] = message;
    }
  }
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
}

function getMessageFromPayload(errorData: unknown): string | null {
  if (!errorData) {
    return null;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData !== 'object') {
    return null;
  }

  const obj = errorData as ApiErrorData;
  return obj.message || obj.error || null;
}

// ============================================================================
// API call
// ============================================================================

const OFFERS_ENDPOINT = 'offer-management/offers';

export const editOffer = async (
  input: EditOfferInput
): Promise<EditOfferResult> => {
  try {
    const payload: EditOfferPayload = {
      offerName: input.offerName.trim(),
      subscriptionPlanIds: input.subscriptionPlanIds,
      discountType: mapDiscountType(input.discountType),
      discountValue: parseDiscountValue(input.discountValue),
      startDate: input.startDate ? format(input.startDate, 'yyyy-MM-dd') : '',
      endDate: input.endDate ? format(input.endDate, 'yyyy-MM-dd') : '',
      status: input.status,
    };

    const result = await apiService.patchAPI(
      `${OFFERS_ENDPOINT}/${input.offerId}`,
      payload,
      { skipGlobalErrorRedirect: true }
    );

    if (!result.error) {
      return { success: true, data: result.data };
    }

    const errorMessage = extractErrorMessage(result.data, result.status);
    const fieldErrors = getFieldErrorsFromPayload(result.data);

    return {
      success: false,
      error: errorMessage,
      ...(fieldErrors && { fieldErrors }),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const errorData = error.response?.data;
      const errorMessage = extractErrorMessage(errorData, status);
      const fieldErrors = getFieldErrorsFromPayload(errorData);
      return {
        success: false,
        error: errorMessage,
        ...(fieldErrors && { fieldErrors }),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : OFFER_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
